import React, { useState } from 'react';
import { createTeam, TeamResponse, updateTeam } from '../../api/team';
import { Button } from '../../styles/common/Button.styled';
import { ConsoleInput, ConsoleLabel, FormGroup, FieldError } from '../../styles/common/input.styled';
import { getErrorMessage, getResponseMessage } from '../../utils/apiMessage';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';

interface TeamModalProps {
  team?: TeamResponse;
  onClose: () => void;
  onSuccess: () => void;
}

const TeamModal: React.FC<TeamModalProps> = ({ team, onClose, onSuccess }) => {
  const isEditMode = Boolean(team);
  const [displayName, setDisplayName] = useState(team?.displayName ?? '');
  const [error, setError] = useState('');
  const [isPending, setIsPending] = useState(false);

  const handleDisplayNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayName(event.target.value);
    if (error) setError('');
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedDisplayName = displayName.trim();
    if (!trimmedDisplayName) {
      setError('팀명을 입력해주세요.');
      return;
    }

    try {
      setIsPending(true);
      const response = team
        ? await updateTeam(team.id, { displayName: trimmedDisplayName })
        : await createTeam({ displayName: trimmedDisplayName });

      toast.success(getResponseMessage(
        response,
        isEditMode ? '팀 정보가 수정되었습니다.' : '팀이 등록되었습니다.'
      ));
      onSuccess();
    } catch (requestError) {
      toast.error(getErrorMessage(
        requestError,
        isEditMode ? '팀 정보 수정에 실패했습니다.' : '팀 등록에 실패했습니다.'
      ));
    } finally {
      setIsPending(false);
    }
  };

  const title = isEditMode ? '팀 정보 수정' : '신규 팀 등록';
  const actionLabel = isEditMode ? '수정하기' : '등록하기';

  return (
    <Modal title={title} onClose={onClose} width={420} footer={<>
      <Button type="button" $variant="secondary" onClick={onClose}>취소</Button>
      <Button type="submit" form="team-form" disabled={isPending}>
        {isPending ? (isEditMode ? '수정 중...' : '등록 중...') : actionLabel}
      </Button>
    </>}>
      <form id="team-form" onSubmit={handleSubmit}>
        <FormGroup>
          <ConsoleLabel htmlFor="team-display-name">팀명</ConsoleLabel>
          <ConsoleInput
            id="team-display-name"
            name="displayName"
            value={displayName}
            onChange={handleDisplayNameChange}
            placeholder="팀명을 입력하세요"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'team-display-name-error' : undefined}
          />
          {error && <FieldError id="team-display-name-error">{error}</FieldError>}
        </FormGroup>

      </form>
    </Modal>
  );
};

export default TeamModal;
